import { assertNever } from "@msys/common";
import { CardContainer } from "@msys/ui";
import { Box, ButtonBase, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ApplyItemActionInput } from "../../../../clients/graphqlTypes.js";
import { PlaceholderSelection } from "../../doc-items/selection/PlaceholderSelection.js";
import { noopRefetchWizardQuestions } from "../../doc-items/withWizardRefetch.js";
import { PlaceholderSelection_ItemFragment } from "../../doc-items/selection/PlaceholderSelection.generated.js";
import { WizardQuestions_QuestionFragment } from "../../documents/WizardQuestions.generated.js";
import { getItemQuestionSteps } from "./questionShell.js";
import { WizardRendererRef } from "./WizardRendererProps.js";
import { WizardContingencyRenderer } from "./WizardContingencyRenderer.js";
import { WizardDecisionRenderer } from "./WizardDecisionRenderer.js";
import { WizardPropertiesRenderer } from "./WizardPropertiesRenderer.js";
import { WizardRecommendationsRenderer } from "./WizardRecommendationsRenderer.js";

interface Props {
  projectId: string;
  quoteId: string;
  questions: Array<WizardQuestions_QuestionFragment>;
  question: WizardQuestions_QuestionFragment | null;
  /**
   * The item the URL selects, shown in the idle state when it has no question.
   * No selection is the done state: nothing is left to answer in this mode.
   */
  selectedItem: { id: string; title: string } | null;
  /** True while the document or the question list is still being read. */
  loading: boolean;
  /** True when the document or the question list failed to load. */
  loadError: boolean;
  /** The document item behind the current question, for the placeholder panels. */
  placeholderItem: PlaceholderSelection_ItemFragment | null;
  expandedItemIds: string[] | undefined;
  onAnswer: (questionId: string, action: ApplyItemActionInput) => Promise<void>;
  onApplied: (questionId: string) => Promise<void>;
  onSelectQuestion: (question: WizardQuestions_QuestionFragment) => void;
  busy: boolean;
  /** Locks the step bar under the same condition as the action bar. */
  navigationLocked: boolean;
  /**
   * Handed to a renderer that stages its answer, so the action bar's forward
   * button can submit it.
   */
  stagedRef: React.RefObject<WizardRendererRef>;
}

/**
 * One question at a time, headed by its item's title, its label and a step
 * bar over the item's questions; the renderer per kind is the only thing that
 * varies.
 */
export const WizardQuestionShell = ({
  projectId,
  quoteId,
  questions,
  question,
  selectedItem,
  loading,
  loadError,
  placeholderItem,
  expandedItemIds,
  onAnswer,
  onApplied,
  onSelectQuestion,
  busy,
  navigationLocked,
  stagedRef,
}: Props) => {
  const { t } = useTranslate(["Global", "Quote", "QuoteItem"]);

  const generalLabel = t("General", { ns: "Quote" });
  const steps = question
    ? getItemQuestionSteps(questions, question.questionId)?.map(step => ({
        ...step,
        label: step.label ?? generalLabel,
      }))
    : undefined;

  const answer = React.useCallback(
    async (action: ApplyItemActionInput) => {
      if (question) await onAnswer(question.questionId, action);
    },
    [onAnswer, question]
  );

  return (
    <CardContainer
      title={
        question
          ? undefined
          : (selectedItem?.title ??
            (loading || loadError
              ? t("Wizard", { ns: "Quote" })
              : t("No further open questions", { ns: "Quote" })))
      }
      isExpandable={false}
    >
      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        {question && steps && (
          <Stack direction="column" spacing={0.5}>
            <Typography variant="overline" color="text.secondary">
              {question.itemTitle}
            </Typography>
            <Typography variant="h6">
              {steps.find(step => step.active)?.label}
            </Typography>
            <QuestionStepBar
              steps={steps}
              disabled={navigationLocked}
              onSelect={onSelectQuestion}
            />
          </Stack>
        )}

        <Box>
          {question ? (
            <QuestionRenderer
              key={question.questionId}
              projectId={projectId}
              quoteId={quoteId}
              question={question}
              placeholderItem={placeholderItem}
              expandedItemIds={expandedItemIds}
              onAnswer={answer}
              onApplied={() => onApplied(question.questionId)}
              disabled={busy}
              stagedRef={stagedRef}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {loadError
                ? t("The wizard questions could not be loaded", { ns: "Quote" })
                : selectedItem
                  ? t("This item has no open questions", { ns: "Quote" })
                  : loading
                    ? t("Select an item to see its questions", { ns: "Quote" })
                    : t(
                        "Every question in this wizard mode has been answered",
                        {
                          ns: "Quote",
                        }
                      )}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContainer>
  );
};

/**
 * Equal, fixed-size segments, so stepping only recolours them and no label can
 * move the bar. Labels live in the tooltips; the active one is the headline.
 */
const QuestionStepBar = ({
  steps,
  disabled,
  onSelect,
}: {
  steps: Array<{
    question: WizardQuestions_QuestionFragment;
    label: string;
    active: boolean;
  }>;
  disabled: boolean;
  onSelect: (question: WizardQuestions_QuestionFragment) => void;
}) => {
  const activeIndex = steps.findIndex(step => step.active);

  return (
    <Stack direction="row" spacing={0.5} data-testid="question-step-bar">
      {steps.map((step, index) => {
        const filled = index <= activeIndex;

        return (
          // The span keeps the tooltip working on a disabled segment.
          <Tooltip key={step.question.questionId} title={step.label}>
            <Box component="span" sx={{ flex: "1 1 0", minWidth: 0 }}>
              <ButtonBase
                aria-label={step.label}
                aria-current={step.active ? "step" : undefined}
                disabled={disabled}
                onClick={() => {
                  if (!step.active) onSelect(step.question);
                }}
                sx={{ width: "100%", minHeight: 44 }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: filled ? "primary.main" : "divider",
                  }}
                />
              </ButtonBase>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

const QuestionRenderer = ({
  projectId,
  quoteId,
  question,
  placeholderItem,
  expandedItemIds,
  onAnswer,
  onApplied,
  disabled,
  stagedRef,
}: {
  projectId: string;
  quoteId: string;
  question: WizardQuestions_QuestionFragment;
  placeholderItem: PlaceholderSelection_ItemFragment | null;
  expandedItemIds: string[] | undefined;
  onAnswer: (action: ApplyItemActionInput) => Promise<void>;
  onApplied: () => Promise<void>;
  disabled: boolean;
  stagedRef: React.RefObject<WizardRendererRef>;
}) => {
  switch (question.__typename) {
    case "WizardProductQuestion":
    case "WizardPlaceholderForTemplateQuestion": {
      // The panels persist through their own mutations; the shell only treats
      // a fill as the answer signal and steps on it, and the step refetches.
      return placeholderItem ? (
        <PlaceholderSelection
          key={`${placeholderItem.id}:${question.__typename}`}
          kind={
            question.__typename === "WizardProductQuestion"
              ? "product"
              : "template"
          }
          projectId={projectId}
          docId={quoteId}
          item={placeholderItem}
          expandedItemIds={expandedItemIds}
          refetchWizardQuestions={noopRefetchWizardQuestions}
          handleClose={() => {}}
          onApplied={onApplied}
        />
      ) : null;
    }
    case "WizardDecisionQuestion": {
      return (
        <WizardDecisionRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    case "WizardMultiplePropertiesQuestion": {
      return (
        <WizardPropertiesRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
          hideGroupHeading
        />
      );
    }
    case "WizardContingencyQuestion": {
      return (
        <WizardContingencyRenderer
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    case "WizardMultipleRecommendationsQuestion": {
      return (
        <WizardRecommendationsRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    }
    default: {
      return assertNever(question);
    }
  }
};
